import { createSelector } from "reselect";
import {
  EditItemsProps,
  LineItemTotalsType,
  LineItemType,
} from "../types/line-items";
import { RootState } from "../reducers";
import { getAllProductsById } from "./product";
import { preferredLocaleSelector } from "./user";
import { getProductVariations, variations } from "../utils/variations";
import { basketFilter } from "../utils/options";

type LineItemsState = RootState;

function lineItemsSelector(state: LineItemsState): LineItemType[] {
  return state.lineItems;
}

const getProductIdsFromLineItems = (state: LineItemsState): number[] =>
  state.lineItems
    .filter((v) => v._isUnavailable !== true)
    .map<number>(
      ({ productId, variationId }: LineItemType) => variationId || productId,
    );

export const userLineItems = createSelector(
  lineItemsSelector,
  getAllProductsById,
  preferredLocaleSelector,
  (lineItems, products, preferredLocale = "en") => {
    if (!lineItems) {
      return null;
    }

    return lineItems.map((item) => {
      if (!products) {
        return item;
      }
      const product = products[item.productId];
      if (!product) {
        return {
          ...item,
          isVariationItem: false,
          title: "Loading...",
          price: 0,
          total: 0,
        };
      }
      const isVariationItem =
        getProductVariations(product, products, variations(preferredLocale))
          ?.length > 0;

      const lineItemDetails = {
        title: "",
        price: 0,
        total: 0,
      };
      if (item.variationId !== null && item.variationId !== undefined) {
        const variationItem = products[`${item.variationId}`];
        lineItemDetails.title = variationItem.name;
        lineItemDetails.price = variationItem.price;
        lineItemDetails.total = variationItem.price * item.quantity;
      } else if (product) {
        lineItemDetails.title = product.name;
        lineItemDetails.price = product.price;
        lineItemDetails.total = product.price * item.quantity;
      }

      return {
        ...item,
        isVariationItem,
        ...lineItemDetails,
      };
    });
  },
);

export const getBasketItems = createSelector(userLineItems, (lineItems) =>
  lineItems ? lineItems.filter(basketFilter) : [],
);

const lineItemsTotal = createSelector(getBasketItems, (lineItems) => {
  if (!lineItems || lineItems.length === 0) {
    return { priceTotal: 0, quantityTotal: 0 };
  }
  return lineItems.reduce<LineItemTotalsType>(
    (acc, item: LineItemType & { price?: number }) => {
      const itemPrice = item.price || 0;
      return {
        priceTotal: itemPrice * item.quantity + acc.priceTotal,
        quantityTotal: item.quantity + acc.quantityTotal,
      };
    },
    { priceTotal: 0, quantityTotal: 0 },
  );
});

const lineItemProducts = createSelector(userLineItems, (lineItems) => {
  return lineItems?.map(({ productId, variationId, quantity }) => ({
    productId: variationId || productId,
    quantity,
  }));
});

const getIndividualItems = createSelector(
  [
    (state: LineItemsState) => state.lineItems,
    (_: LineItemsState, { lineItemId }: Pick<EditItemsProps, "lineItemId">) =>
      Number(lineItemId),
  ],
  (lineItems, parentId): LineItemType[] =>
    lineItems.filter((lineItem) => lineItem.parentId === parentId) || [],
);

const getDanglingLineItemByParent = createSelector(
  [
    (state: LineItemsState) => state.lineItems,
    (_: LineItemsState, { lineItemId }: Pick<EditItemsProps, "lineItemId">) =>
      Number(lineItemId),
  ],
  (lineItems, lineItemId): LineItemType | undefined => {
    const line =
      lineItems.find((lineItem) => lineItem.id === lineItemId) || undefined;
    if (line && line.parentId) {
      const parentLine = lineItems.find((item) => item.id === line.parentId);
      if (
        parentLine &&
        parentLine.children?.length === 2 &&
        parentLine.children.includes(lineItemId)
      ) {
        const danglingLine = lineItems.find(
          (lineItem) =>
            lineItem.id !== lineItemId && lineItem.parentId === line.parentId,
        );
        if (danglingLine) {
          return danglingLine;
        }
      }
    }
    return;
  },
);

export {
  getProductIdsFromLineItems,
  lineItemsSelector,
  lineItemsTotal,
  lineItemProducts,
  getIndividualItems,
  getDanglingLineItemByParent,
};
